import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useDispatch } from "react-redux";
import { performLogout } from "@src/auth/utils/logout";
import { warningSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { i18n } from "@src/i18n/i18n";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";

const EXPIRY_CHECK_INTERVAL_MS = 60_000;

/**
 * Centralized token expiry check: every minute, compare the token exp with the
 * clock. When the token is expired, run the logout cleanup and navigate to
 * /logout with a warning snackbar.
 */
export const useTokenExpiryCheck = () => {
  const auth = useAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    const interval = setInterval(() => {
      const user = auth.user;

      // no user or no exp information -> nothing to check
      if (!user || user.expires_at === undefined) return;

      // expires_at is in seconds
      if (user.expires_at * 1000 > Date.now()) return; // token still valid

      clearInterval(interval); // tokens are dead, log out only once

      warningSnackbar(i18n("pages.refreshTokenExpiredError.title"));

      // same cleanup as the logout button in the navigation bar,
      // and navigate to /logout even if the cleanup fails
      performLogout(auth, dispatch)
        .catch((error) =>
          console.error("Logout after token expiry failed", error),
        )
        .finally(() => navigateTo(Route.LOGOUT));
    }, EXPIRY_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [auth, dispatch]);
};
